import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  GoogleMap, Polyline, Marker, useJsApiLoader,
} from '@react-google-maps/api';
import _ from 'lodash';
import moment from 'moment/moment';
import { NavLink } from 'react-router-dom';
import classes from '../../_common/Modal/style.module.scss';
import Modal from '../../_common/Modal/Modal';
import apiConfig from '../../../config';
import Api from '../../../api/api';
import Checkbox from '../../_common/Form/Checkbox/Checkbox';
import Loader from '../../_common/Loader/Loader';
import StatusField from '../../_common/StatusField/StatusField';
import { ReactComponent as TimeSvg } from '../../../assets/icons/time.svg';
import { ReactComponent as AddressSvg } from '../../../assets/icons/home.svg';

const { MAP_KEY } = apiConfig;

const containerStyle = {
  width: '100%',
  height: 'calc(100% - 20px)',
};

const STATUS_COLORS = {
  Started: '#6C63FF',
  Pending: '#FFC107',
  Completed: '#00C853',
};

const polylineDecode = (str) => {
  let index = 0;
  let lat = 0;
  let lng = 0;
  const coordinates = [];
  let shift = 0;
  let result = 0;
  let byte = null;
  let latitudeChange;
  let longitudeChange;

  while (index < str.length) {
    byte = null;
    shift = 0;
    result = 0;

    do {
      byte = str.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    latitudeChange = ((result & 1) ? ~(result >> 1) : (result >> 1));

    shift = result = 0;

    do {
      byte = str.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    longitudeChange = ((result & 1) ? ~(result >> 1) : (result >> 1));

    lat += latitudeChange;
    lng += longitudeChange;

    coordinates.push([lat / 1E5, lng / 1E5]);
  }

  return coordinates.map(([lat, lng]) => ({ lat, lng }));
};

const USA_CENTER = { lat: 39.8283, lng: -98.5795 };
const USA_ZOOM = 4;

const RouteModal = ({ mapInfo, date, onClose }) => {
  const { userId, appointments } = mapInfo || { };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: MAP_KEY,
  });

  const mapRef = useRef(null);

  const [visibleStatuses, setVisibleStatuses] = useState([]);

  const [points, setPoints] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [pastRoad, setPastRoad] = useState([]);

  const [userInfoModal, setUserInfoModal] = useState({});

  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    if (userId) {
      (async () => {
        loadingToggle(true);

        const [{ data: locationData }, { data: dailyData }] = await Promise.all([
          Api.getRepLocation({ userId, date: moment(date).toISOString() }),
          Api.getRepLocationDaily({ userId, date: moment(date).toISOString() }),
        ]);

        const route = (locationData.route || []);

        setPoints(
          route.map((p, i) => ({
            id: p.id,
            index: i + 1,
            userId,
            lat: Number(p.lat),
            lng: Number(p.lng),
            status: p.status,
            route: i && route[i - 1]?.polyline ? polylineDecode(route[i - 1]?.polyline) : [],
            date: p.date,
          })),
        );

        if (locationData.location?.lat && locationData.location?.lng) {
          setCurrentLocation({
            lat: Number(locationData.location.lat),
            lng: Number(locationData.location.lng),
          });
        }

        setPastRoad(polylineDecode(dailyData.polyline) || []);

        loadingToggle(false);
      })();
    } else {
      setPoints([]);
      setCurrentLocation(null);
      loadingToggle(true);
    }
  }, [userId]);

  const onMapLoad = (map) => {
    mapRef.current = map;

    setVisibleStatuses([
      'Started',
      'Pending',
      'Completed',
    ]);
  };

  const filteredPoints = useMemo(
    () => points.filter((p) => visibleStatuses.includes(p.status)),
    [points, visibleStatuses],
  );

  const getIndexIcon = (color) => {
    if (!isLoaded || !window.google) return null;

    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 3,
      scale: 14,
      labelOrigin: new window.google.maps.Point(0, 0),
    };
  };

  const getIndexLabel = (index) => ({
    text: String(index),
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '700',
  });

  const userLocationIcon = useMemo(() => {
    if (!isLoaded || !window.google) return null;

    return {
      url: `data:image/svg+xml;utf8,${encodeURIComponent(`
           <svg fill="#d82e2e" height="80" width="80" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg"
     xmlns:xlink="http://www.w3.org/1999/xlink"
     viewBox="0 0 394.254 394.254" xml:space="preserve">
<path d="M197.127,0c-77.197,0-140,62.806-140,140.002c0,31.703,20.895,81.688,63.879,152.813
\tc30.721,50.832,60.926,92.583,62.197,94.335c3.236,4.46,8.412,7.104,13.924,7.104c5.512,0,10.689-2.644,13.926-7.104
\tc1.27-1.752,31.477-43.503,62.195-94.335c42.984-71.125,63.879-121.11,63.879-152.813C337.127,62.806,274.324,0,197.127,0z
\t M201.801,172.82c-0.057-0.538,0.119-1.075,0.482-1.474c0.199-0.224,0.447-0.395,0.719-0.502v-4.763
\tc0-0.929,0.666-1.721,1.576-1.883c1.518-0.27,3.076-0.825,4.639-1.648c0.594-0.313,1.307-0.295,1.881,0.051
\tc0.574,0.346,0.924,0.967,0.924,1.639l0.002,11.419c0,0.418-0.135,0.82-0.385,1.15l-5.363,7.12
\tc-0.367,0.486-0.938,0.762-1.527,0.762c-0.17,0-0.344-0.024-0.512-0.073c-0.758-0.208-1.309-0.862-1.389-1.644L201.801,172.82z
\t M202.26,154.088h-10.266c-2.27,0-6.65-3.033-11.322-9.814c-3.691-5.349-6.256-11.266-6.859-15.833l-0.104-0.791l-1.854-1.207
\tc-0.543-0.352-0.871-0.954-0.871-1.6v-3.147c0-1.058,0.857-1.911,1.912-1.911h0.781v-3.41c0-0.722,0.408-1.384,1.057-1.708
\tc2.631-1.314,7.854-3.518,13.26-3.518c4.314,0,7.891,1.437,10.631,4.263c3.414,3.53,7.35,5.32,11.697,5.32
\tc2.461,0,5.006-0.582,7.563-1.726c0.59-0.267,1.273-0.213,1.818,0.139c0.256,0.165,0.463,0.387,0.609,0.64h1.045
\tc1.055,0,1.91,0.854,1.91,1.911v3.147c0,0.646-0.326,1.248-0.869,1.6l-1.855,1.207l-0.102,0.785
\tc-0.605,4.572-3.168,10.49-6.861,15.84C208.908,151.056,204.529,154.088,202.26,154.088z M191.971,171.347
\tc0.363,0.398,0.539,0.936,0.482,1.474l-1.047,10.156c-0.08,0.785-0.631,1.437-1.389,1.647c-0.17,0.048-0.342,0.07-0.512,0.07
\tc-0.592,0-1.16-0.273-1.527-0.761l-5.371-7.124c-0.25-0.332-0.385-0.734-0.385-1.15l0.008-11.422c0-0.67,0.352-1.291,0.926-1.637
\ts1.285-0.365,1.879-0.05c1.563,0.823,3.123,1.379,4.639,1.648c0.912,0.162,1.576,0.954,1.576,1.883v4.763
\tC191.523,170.952,191.772,171.123,191.971,171.347z M258.922,199.752c-4.084-15.856-9.832-31.474-16.93-33.513l-21.26-6.444
\tc-0.514-1.557-1.652-2.898-3.244-4.034c1.752-1.947,3.23-3.919,4.355-5.55c2.668-3.869,4.887-8.034,6.426-12.056
\tc0.758-1.697,1.402-3.398,1.926-5.07c2.07-1.732,3.289-4.323,3.289-7.043v-6.658c0-1.909-0.602-3.779-1.705-5.328v-9.372
\tc0-16.443-13.379-29.818-29.82-29.818h-9.664c-16.441,0-29.82,13.375-29.82,29.818v9.373c-1.105,1.55-1.705,3.42-1.705,5.327v6.658
\tc0,2.715,1.219,5.306,3.289,7.041c0.521,1.672,1.168,3.375,1.926,5.076c1.537,4.018,3.758,8.184,6.426,12.051
\tc1.125,1.63,2.604,3.603,4.355,5.551c-1.592,1.136-2.73,2.478-3.244,4.034l-21.26,6.444c-7.098,2.039-12.846,17.656-16.93,33.513
\tc-14.977-15.483-24.207-36.558-24.207-59.75c0-47.422,38.58-86.004,86.002-86.004c47.422,0,86.002,38.582,86.002,86.004
\tC283.129,163.194,273.898,184.268,258.922,199.752z"/>
</svg>
      `)}`,
      scaledSize: new window.google.maps.Size(36, 36),
      anchor: new window.google.maps.Point(18, 18),
    };
  }, [isLoaded]);

  const center = currentLocation
    || filteredPoints[0]
    || USA_CENTER;

  const toggleStatus = (status) => {
    setVisibleStatuses((prev) => (prev.includes(status)
      ? prev.filter((s) => s !== status)
      : [...prev, status]));
  };

  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    if (!filteredPoints.length && !currentLocation) {
      mapRef.current.setZoom(USA_ZOOM);

      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    filteredPoints.forEach((p) => {
      bounds.extend({ lat: p.lat, lng: p.lng });
    });

    if (currentLocation) {
      bounds.extend(currentLocation);
    }

    mapRef.current.fitBounds(bounds, {
      top: 60,
      bottom: 60,
      left: 40,
      right: 40,
    });

    window.google.maps.event.addListenerOnce(
      mapRef.current,
      'bounds_changed',
      () => {
        const zoom = mapRef.current.getZoom();
        if (zoom > 15) mapRef.current.setZoom(15);
      },
    );
  }, [filteredPoints, currentLocation]);

  const address = useMemo(() => (
    _.compact([userInfoModal?.physician?.addressStreet, userInfoModal?.physician?.addressCity,
      userInfoModal?.physician?.addressState, userInfoModal?.physician?.addressZipCode])
  ), []);
  console.log(userInfoModal, 777, appointments);
  return (
    <Modal open={!!userId} onClose={onClose} className={classes.route_modal}>
      {isLoaded && !loading ? (
        <>
          <GoogleMap
            onLoad={onMapLoad}
            mapContainerStyle={containerStyle}
            center={center}
            zoom={11}
          >
            {filteredPoints.map((p, i) => (
              <Polyline
                key={i}
                path={p.route}
                options={{
                  strokeColor: STATUS_COLORS[p.status],
                  strokeOpacity: 1,
                  strokeWeight: 4,
                }}
              />
            ))}

            <Polyline
              path={pastRoad}
              options={{
                strokeColor: 'red',
                strokeOpacity: 1,
                strokeWeight: 4,
              }}
            />

            {currentLocation && userLocationIcon && (
              <Marker
                position={currentLocation}
                icon={userLocationIcon}
                zIndex={3000}
              />
            )}

            {filteredPoints.map((p) => (
              <Marker
                key={p.index}
                position={{ lat: p.lat, lng: p.lng }}
                icon={getIndexIcon(STATUS_COLORS[p.status])}
                label={getIndexLabel(p.index)}
                zIndex={2000}
                onClick={() => setUserInfoModal(appointments.find((a) => a.id === p.id))}
              />
            ))}
          </GoogleMap>

          <div className={classes.route_filters}>
            {Object.keys(STATUS_COLORS).map((status) => (
              <Checkbox
                key={status}
                checked={visibleStatuses.includes(status)}
                onChange={() => toggleStatus(status)}
                label={(
                  <span style={{ color: STATUS_COLORS[status], fontWeight: 600 }}>
                    {status}
                  </span>
                )}
              />
            ))}
          </div>
        </>
      )

        : <Loader size={60} borderWidth={4} />}

      <Modal
        open={!!userInfoModal.id}
        zIndex={10000000}
        onClose={() => setUserInfoModal({})}
        className={classes.route_info_modal}
      >
        <h5>
          {userInfoModal.Rep}
        </h5>

        <ul className={classes.info_wrapper}>
          <li>
            <TimeSvg className={classes.time_svg} />

            {moment.utc(userInfoModal.dateTime).local().format('hh:mm a')}
            {' '}

            <NavLink to={`/physicians/${userInfoModal.PhysicianId}`}>
              <span className={classes.physician_name}>
                {userInfoModal.Physician || userInfoModal.PhysicianName}
              </span>
            </NavLink>

            {' '}
            {userInfoModal.group && (
            <span className={classes.group_name}>
              (
              {userInfoModal.group}
              )
            </span>
            )}

            <div className={classes.actual_time}>
              <TimeSvg />
              Check in time
              {' '}

              {moment.utc(userInfoModal.actualDateTime || userInfoModal.dateTime).local().format('hh:mm a')}
            </div>

            {!!address.length && (
            <div className={classes.address}>
              <AddressSvg className={classes.address_svg} />

              {address.join(', ')}
            </div>
            )}

            <div className={classes.notes}>
              {userInfoModal.visitNotes?.map((v) => (
                v.fileUrl
                  ? (
                    <a
                      href={v.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={`${classes.note} ${classes.file}`}
                    >
                      {v.note}
                    </a>
                  )

                  : (
                    <div key={v.id} className={classes.note}>
                      {v.note}
                    </div>
                  )
              ))}

              <div className={classes.check_status}>
                Check in Type:
                {' '}
                <b>{userInfoModal.checkInStatus}</b>
              </div>

              <div className={classes.status_wrapper}>
                <StatusField status={userInfoModal.status} className={classes.status_field} />
              </div>
            </div>
          </li>
        </ul>
      </Modal>
    </Modal>
  );
};

RouteModal.propTypes = {
  mapInfo: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RouteModal;
